import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import {
  ArrowLeft,
  Bell,
  Mail,
  Lock,
  Shield,
  Save,
  AlertCircle,
  Globe,
  Users
} from 'lucide-react';

export default function CompanySettings() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    notifications: {
      emailNotifications: true,
      submissionUpdates: true,
      marketingEmails: false,
      analystMessages: true,
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: '30',
    },
    preferences: {
      language: 'en',
      timezone: 'Africa/Nairobi',
      dateFormat: 'DD/MM/YYYY',
    },
    api: {
      enableApi: false,
      webhookUrl: '',
    },
  });

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      toast({
        title: 'Settings saved',
        description: 'Your preferences have been updated.',
      });
    }, 1000);
  };

  return (
    <div className="space-y-6 w-full max-w-4xl mx-auto">
      <Button variant="ghost" onClick={() => navigate('/company/dashboard')} className="gap-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </Button>

      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Company Settings</h1>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Changes to these settings will affect your entire company account.
        </AlertDescription>
      </Alert>

      <div className="space-y-6">
        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive notifications via email
                </p>
              </div>
              <Switch
                checked={settings.notifications.emailNotifications}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, emailNotifications: checked }
                })}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Submission Updates</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when your submissions change status
                </p>
              </div>
              <Switch
                checked={settings.notifications.submissionUpdates}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, submissionUpdates: checked }
                })}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Analyst Messages</Label>
                <p className="text-sm text-muted-foreground">
                  Receive messages from analysts reviewing your submissions
                </p>
              </div>
              <Switch
                checked={settings.notifications.analystMessages}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, analystMessages: checked }
                })}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Marketing Emails</Label>
                <p className="text-sm text-muted-foreground">
                  Receive updates about V&V programs and opportunities
                </p>
              </div>
              <Switch
                checked={settings.notifications.marketingEmails}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, marketingEmails: checked }
                })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Security Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Two-Factor Authentication</Label>
                <p className="text-sm text-muted-foreground">
                  Add an extra layer of security to your account
                </p>
              </div>
              <Switch
                checked={settings.security.twoFactorAuth}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  security: { ...settings.security, twoFactorAuth: checked }
                })}
              />
            </div>
            <Separator />
            <div className="space-y-2">
              <Label>Session Timeout (minutes)</Label>
              <Input
                type="number"
                value={settings.security.sessionTimeout}
                onChange={(e) => setSettings({
                  ...settings,
                  security: { ...settings.security, sessionTimeout: e.target.value }
                })}
                min="5"
                max="120"
              />
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Regional Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Language</Label>
              <select
                className="w-full p-2 border rounded-md bg-background"
                value={settings.preferences.language}
                onChange={(e) => setSettings({
                  ...settings,
                  preferences: { ...settings.preferences, language: e.target.value }
                })}
              >
                <option value="en">English</option>
                <option value="sw">Swahili</option>
                <option value="fr">French</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <select
                className="w-full p-2 border rounded-md bg-background"
                value={settings.preferences.timezone}
                onChange={(e) => setSettings({
                  ...settings,
                  preferences: { ...settings.preferences, timezone: e.target.value }
                })}
              >
                <option value="Africa/Nairobi">Nairobi (UTC+3)</option>
                <option value="Africa/Johannesburg">Johannesburg (UTC+2)</option>
                <option value="Africa/Lagos">Lagos (UTC+1)</option>
                <option value="Africa/Cairo">Cairo (UTC+2)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Date Format</Label>
              <select
                className="w-full p-2 border rounded-md bg-background"
                value={settings.preferences.dateFormat}
                onChange={(e) => setSettings({
                  ...settings,
                  preferences: { ...settings.preferences, dateFormat: e.target.value }
                })}
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* API Access */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              API Access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable API Access</Label>
                <p className="text-sm text-muted-foreground">
                  Allow programmatic access to your submissions
                </p>
              </div>
              <Switch
                checked={settings.api.enableApi}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  api: { ...settings.api, enableApi: checked }
                })}
              />
            </div>
            {settings.api.enableApi && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label>Webhook URL</Label>
                  <Input
                    placeholder="https://your-company.com/webhook"
                    value={settings.api.webhookUrl}
                    onChange={(e) => setSettings({
                      ...settings,
                      api: { ...settings.api, webhookUrl: e.target.value }
                    })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Receive real-time updates when your submission status changes
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}